import { Button, Form, Modal, Row, Table } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import React, { useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import UploadComponent from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import TranslateText from 'components/hoc/TranslateText';
import { IPodpisFileList } from 'components/pages/Dokumenty/Podpisy';

import isinUpload, {
    EIsinRegistraceSouborTyp,
    IIsinRegistraceSoubor,
    isinRegistraceSoubory
} from 'services/akcionar/emise/isinUpload';
import fileDownload from 'services/fileDownload';
import { prepareFileIsinApi } from 'services/prepareFile';

import typPrilohyRegistraceSelect from 'data/codebooks/emise/udaje/typPrilohyRegistraceSelect';

import FormWrapper from '../FormWrapper';

interface IPrilohyTableEmise {
    zadostID: number;
    required?: boolean;
    prilohyData: IIsinRegistraceSoubor[];
    setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
}

const PrilohyTableEmise: React.FC<IPrilohyTableEmise> = ({ zadostID, required, prilohyData, setRefetch }) => {
    const [fileList, setFileList] = useState<IPodpisFileList[]>([]);
    const [typPrilohy, setTypPrilohy] = useState<EIsinRegistraceSouborTyp>();
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [form] = Form.useForm();

    const cols = [
        {
            title: <TranslateText>DOKUMENT_DODATEK_DETAIL_PRILOHY_TABLE</TranslateText>,
            dataIndex: 'typ',
            key: 'typ',
            render: (text: string) => <TranslateText>{'ISIN_REGISTRACE_SOUBOR_' + text}</TranslateText>
        },
        {
            title: <TranslateText>NAZEV</TranslateText>,
            dataIndex: 'orignazev',
            key: 'orignazev'
        },
        {
            render: (_text: string, row: IIsinRegistraceSoubor, index: number) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'right'
                    }}
                >
                    <AnchorComponent
                        id={`OsobyZobrazitBtn${index}`}
                        key={`OsobyZobrazitBtn${index}`}
                        onClick={() => {
                            setLoading(true);
                            isinUpload('delete', zadostID, undefined, row.oid)
                                .then(() => setRefetch(pv => !pv))
                                .finally(() => setLoading(false));
                        }}
                        style={{
                            marginRight: 8
                        }}
                    >
                        <TranslateText>SMAZAT</TranslateText>
                    </AnchorComponent>

                    <AnchorComponent
                        id={`OsobyZobrazitBtn${index}`}
                        key={`OsobyZobrazitBtn${index}`}
                        onClick={() => {
                            setLoading(true);
                            prepareFileIsinApi(zadostID, row.tempnazev ? row.tempnazev : '')
                                .then(resp => {
                                    fileDownload(resp.redirect, row.orignazev ? row.orignazev : 'file');
                                })
                                .finally(() => setLoading(false));
                        }}
                    >
                        <TranslateText>STAHNOUT</TranslateText>
                    </AnchorComponent>
                </div>
            )
        }
    ];

    const uploadPropsPotvrzeni = {
        id: 'potvrzeniDownloadBtn',
        showDownloadIcon: false,
        onChange: (upldata: UploadChangeParam) => {
            if (upldata.fileList) {
                upldata.fileList.length && setLoading(true);
                setFileList(
                    upldata.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error',
                                tempnazev: e.response && e.response?.tempnazev ? e.response.tempnazev : undefined
                            };
                        })
                );
                form.validateFields(['typprilohy']);
            }
            if (upldata.file.response) {
                setLoading(false);
            }
        },
        onRemove: () => {
            setFileList([]);
        },
        fileList: fileList,
        buttonDisabled: !!fileList.length
    };

    return (
        <>
            <Row>
                <Paragraph
                    text="*"
                    style={{
                        color: '#ff4d4f',
                        fontSize: 25,
                        position: 'relative',
                        top: 2,
                        marginRight: 2,
                        display: required ? 'inherit' : 'none'
                    }}
                />
                <Title level={5} text="DOKUMENT_DODATEK_DETAIL_PRILOHY" />
            </Row>
            <Table
                rowKey={(_, index) => index || 0}
                locale={{
                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                }}
                style={{
                    margin: '30px 0px'
                }}
                columns={cols}
                dataSource={prilohyData}
                pagination={false}
                rowClassName={(_a, i) => {
                    const classes = [];
                    if (i % 2) classes.push('tableRowGray');
                    return classes.join(' ');
                }}
            />
            <Form.Item
                style={{
                    marginBottom: 34
                }}
                name="prilohy"
                rules={
                    required
                        ? [
                              () => ({
                                  validator() {
                                      if (prilohyData?.length) return Promise.resolve();
                                      return Promise.reject(
                                          <TranslateText>NEVALIDNI_POVINNA_POLOZKA_UPLOAD</TranslateText>
                                      );
                                  }
                              })
                          ]
                        : []
                }
            >
                <Button
                    onClick={() => {
                        setOpenModal(true);
                        form.setFieldsValue({
                            typ: undefined
                        });
                    }}
                    id="vlozitPrilohuBtn"
                    type="primary"
                    loading={loading}
                >
                    <TranslateText>DOKUMENT_DODATEK_DETAIL_PRILOHY_BTN</TranslateText>
                </Button>
            </Form.Item>

            <Modal
                title={<TranslateText>NOVA_PRILOHA</TranslateText>}
                open={!!openModal}
                onCancel={() => {
                    setOpenModal(false);
                    setTypPrilohy(undefined);
                    setFileList([]);
                }}
                destroyOnClose={true}
                width={820}
                footer={[
                    <Row>
                        <ButtonComponent
                            buttontext="ZPET"
                            onClick={() => {
                                setOpenModal(false);
                                setTypPrilohy(undefined);
                                setFileList([]);
                            }}
                            id="closeModaltBtn"
                            type="default"
                            disabled={loading}
                        />
                        <ButtonComponent
                            buttontext="ULOZIT"
                            id="OsobyPokracovatBtn"
                            style={{
                                margin: '0 8px'
                            }}
                            type="primary"
                            disabled={!fileList.length || !typPrilohy}
                            loading={loading}
                            onClick={() => {
                                setLoading(true);
                                isinRegistraceSoubory(zadostID, {
                                    //  oid: fileList[0].oid || 0,
                                    typ: typPrilohy!,
                                    tempnazev: fileList[0].tempnazev,
                                    orignazev: fileList[0].name
                                })
                                    .then(() => {
                                        setTypPrilohy(undefined);

                                        setFileList([]);
                                        setRefetch(pv => !pv);
                                        setOpenModal(false);
                                    })
                                    .finally(() => setLoading(false));
                            }}
                        />
                    </Row>
                ]}
            >
                <LoadingWrapper loading={loading}>
                    <FormWrapper outForm={form} onFinish={() => {}}>
                        <SelectComponent
                            labelParagraph="DOKUMENT_DODATEK_DETAIL_PRILOHY_TABLE"
                            onChange={val => setTypPrilohy(val)}
                            defaultValue={typPrilohy}
                            isFormItem={true}
                            data={typPrilohyRegistraceSelect}
                            name="typ"
                        />
                        <div>
                            <Form.Item
                                style={{
                                    marginTop: 16
                                }}
                                id="potvrzeni"
                                name="potvrzeni"
                            >
                                <div
                                    style={{
                                        marginBottom: 22
                                    }}
                                >
                                    <UploadComponent
                                        {...uploadPropsPotvrzeni}
                                        // uploadPath={mode === 'smsLogin' ? apiPaths.FILE_UPLOAD_AMLODKAZ : ''}
                                    />
                                </div>
                            </Form.Item>
                        </div>
                    </FormWrapper>
                </LoadingWrapper>
            </Modal>
        </>
    );
};
export default PrilohyTableEmise;
